import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import '../index.css';

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  const [bio, setBio] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Account type change request
  const [showAccountTypeModal, setShowAccountTypeModal] = useState(false);
  const [newAccountType, setNewAccountType] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [accountChangeLoading, setAccountChangeLoading] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchProfile();
    checkPendingRequest();
  }, [isAuthenticated, navigate]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/profile/me/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBio(response.data.profile?.bio || '');
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile');
    }
  };

  const checkPendingRequest = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/profile/account-type-change/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingRequest(response.data.request);
    } catch (err) {
      // No pending request or error
      setPendingRequest(null);
    }
  };

  const handleAccountTypeChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setAccountChangeLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/profile/account-type-change',
        {
          requestedAccountType: newAccountType,
          reason: changeReason
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess('Account type change request submitted! It will be reviewed by an admin.');
      setShowAccountTypeModal(false);
      setNewAccountType('');
      setChangeReason('');
      checkPendingRequest();
    } catch (err) {
      console.error('Account type change error:', err);
      setError(err.response?.data?.message || 'Failed to submit request');
    } finally {
      setAccountChangeLoading(false);
    }
  };

  const getAccountTypeEmoji = (accountType) => {
    const emojiMap = {
      'regular': '🎮',
      'indie_dev': '👨‍💻',
      'content_creator': '🎬',
      'giveaway_host': '🎁',
      'steam_curator': '🎯',
      'admin': '👑'
    };
    return emojiMap[accountType] || '🎮';
  };

  const getAccountTypeDisplay = (accountType) => {
    const displayMap = {
      'regular': 'Regular User (Gamer)',
      'indie_dev': 'Indie Developer',
      'content_creator': 'Content Creator',
      'giveaway_host': 'Giveaway Host',
      'steam_curator': 'Steam Curator',
      'admin': 'Admin'
    };
    return displayMap[accountType] || 'Regular User';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      await axios.put('/api/profile/me/profile', 
        { bio },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess('Profile updated successfully!');
      setTimeout(() => {
        navigate(`/profile/${user.username}`);
      }, 1500);
    } catch (err) {
      console.error('Update profile error:', err);
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{
        backgroundColor: 'var(--modal-bg)',
        borderRadius: '16px',
        padding: '30px',
        border: `1px solid var(--modal-border)`
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h1 style={{ margin: 0, fontSize: '2rem', color: 'var(--text-dark)' }}>✏️ Edit Profile</h1>
          <button
            onClick={() => navigate(`/profile/${user?.username}`)}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--btn-secondary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Cancel
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--error-bg)',
            color: 'var(--error-text)',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            backgroundColor: 'var(--success-bg)',
            color: 'var(--success-text)',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {success}
          </div>
        )}

        {/* Info Box about Community Card */}
        <div style={{
          backgroundColor: 'var(--info-box-bg)',
          color: 'var(--accent-blue)',
          padding: '16px',
          borderRadius: '12px',
          marginBottom: '25px',
          border: `1px solid var(--info-box-border)`
        }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
            <span style={{ fontSize: '1.5rem' }}>ℹ️</span>
            <div>
              <strong style={{ display: 'block', marginBottom: '8px' }}>Profile Avatar & Social Links</strong>
              <p style={{ margin: '0 0 8px 0', fontSize: '0.95rem' }}>
                Your profile picture and social links are automatically synced from your Community Card.
              </p>
              <Link 
                to="/community" 
                style={{ 
                  color: 'var(--accent-blue)', 
                  fontWeight: 'bold',
                  textDecoration: 'underline'
                }}
              >
                Go to Community Cards to update them →
              </Link>
            </div>
          </div>
        </div>

        {/* Current Account Type & Change Request */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '25px',
          border: `1px solid var(--border-color)`
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', color: 'var(--text-dark)' }}>
            Account Type
          </h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <div>
              <p style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
                <span style={{ fontSize: '1.5rem', marginRight: '8px' }}>
                  {getAccountTypeEmoji(user?.accountType)}
                </span>
                {getAccountTypeDisplay(user?.accountType)}
              </p>
            </div>
            {!pendingRequest && user?.accountType !== 'admin' && (
              <button
                onClick={() => setShowAccountTypeModal(true)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--accent-blue)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}
              >
                Request Change
              </button>
            )}
          </div>

          {pendingRequest && (
            <div style={{
              backgroundColor: 'var(--warning-bg)',
              color: 'var(--warning-text)',
              padding: '12px',
              borderRadius: '8px',
              marginTop: '10px'
            }}>
              <strong>⏳ Pending Request:</strong> You have requested to change to{' '}
              <strong>{getAccountTypeDisplay(pendingRequest.requestedAccountType)}</strong>.
              Your request is awaiting admin approval.
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Bio */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: 'var(--text-dark)' }}>
              Bio
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={500}
              rows={4}
              placeholder="Tell us about yourself..."
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid var(--border-color)`,
                backgroundColor: 'var(--info-box-bg)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
            <p style={{ marginTop: '5px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              {bio.length}/500 characters
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '15px',
              backgroundColor: loading ? 'var(--btn-secondary)' : 'var(--accent-green)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1.1rem',
              fontWeight: 'bold'
            }}
          >
            {loading ? 'Saving...' : '💾 Save Bio'}
          </button>
        </form>
      </div>

      {/* Account Type Change Modal */}
      {showAccountTypeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'var(--modal-bg)',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%',
            border: `1px solid var(--modal-border)`,
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: 'var(--text-dark)' }}>Change Account Type</h2>
              <button
                onClick={() => {
                  setShowAccountTypeModal(false);
                  setNewAccountType('');
                  setChangeReason('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--text-muted)'
                }}
              >
                ✖
              </button>
            </div>

            <form onSubmit={handleAccountTypeChange}>
              <div style={{ marginBottom: '20px' }}>
                <p style={{ color: 'var(--text-primary)', marginBottom: '10px' }}>
                  Current: <strong>{getAccountTypeEmoji(user?.accountType)} {getAccountTypeDisplay(user?.accountType)}</strong>
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: 'var(--text-dark)' }}>
                  New Account Type
                </label>
                <select
                  value={newAccountType}
                  onChange={(e) => setNewAccountType(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid var(--border-color)`,
                    backgroundColor: 'var(--info-box-bg)',
                    color: 'var(--text-primary)',
                    fontSize: '1rem'
                  }}
                >
                  <option value="">Select account type...</option>
                  {user?.accountType !== 'regular' && (
                    <option value="regular">🎮 Regular User (Gamer)</option>
                  )}
                  {user?.accountType !== 'indie_dev' && (
                    <option value="indie_dev">👨‍💻 Indie Developer</option>
                  )}
                  {user?.accountType !== 'content_creator' && (
                    <option value="content_creator">🎬 Content Creator</option>
                  )}
                  {user?.accountType !== 'giveaway_host' && (
                    <option value="giveaway_host">🎁 Giveaway Host</option>
                  )}
                  {user?.accountType !== 'steam_curator' && (
                    <option value="steam_curator">🎯 Steam Curator</option>
                  )}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: 'var(--text-dark)' }}>
                  Reason for Change
                </label>
                <textarea
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="Explain why you want to change your account type..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid var(--border-color)`,
                    backgroundColor: 'var(--info-box-bg)',
                    color: 'var(--text-primary)',
                    fontSize: '1rem',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
                <p style={{ marginTop: '5px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  {changeReason.length}/500 characters
                </p>
              </div>

              <div style={{
                backgroundColor: 'var(--info-box-bg)',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '0.9rem',
                color: 'var(--text-primary)'
              }}>
                ℹ️ Your request will be reviewed by an admin. You'll be notified once it's approved or rejected.
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAccountTypeModal(false);
                    setNewAccountType('');
                    setChangeReason('');
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: 'var(--btn-secondary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={accountChangeLoading}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: accountChangeLoading ? 'var(--btn-secondary)' : 'var(--accent-green)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: accountChangeLoading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  {accountChangeLoading ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProfile;
